import { Component, Event, EventEmitter, Host, h, Method, Prop, State, Watch } from '@stencil/core';
import { fromEvent, debounceTime, Subscription } from 'rxjs';
import { KINDS, NOTIFICATION, SIZES, AutoCompletionResult, Scope, SearchResultResponse, SearchResult, SearchState, Prediction, SmartFilter } from '@local/models'
import { SearchService } from '@local/services'


@Component({
  tag: 'cr-search',
  styleUrl: 'cr-search.css',
  shadow: true,
})
export class CrSearch {

  /**
   * Scopes represent the kinds of items that are available via search
   * @type {Array<string>}
   */
  @Prop() scopes: Array<string> = []

  /**
   * placeholder text
   * @type {string}
   */
  @Prop() placeholder: string = 'What would you like to find today?'

  /**
   * The core service that drives this component. It has a default, but can be
   * swapped out if needed.
   * @type {SearchService}
   */
  @Prop() driver: SearchService = new SearchService()

  /**
   * The auth token to use while making calls
   * @type {string}
   */
  @Prop() authToken: string

  /**
   * An internal indicator of state
   * @type {SearchState}
   */
  @Prop() disableSmartFilters: boolean = false

  /**
   * Focus on the search input.
   */
  @Method()
  async grabFocus() {
    this.textInput.grabFocus()
  }

  @State() searchState: SearchState = SearchState.WaitingForInput

  /**
   * The scopes actually in use
   * @type {Map<string, boolean>}
   */
  @State() activeScopes: Map<string, boolean> = new Map()

  /**
   * Displays the configuration for search
   * @type {boolean}
   */
  @State() configControlExpanded: boolean = false

  /**
   * Displays the autocompletions
   * @type {boolean}
   */
  @State() predictionsExpanded: boolean = false

  /**
   * When predictions are displayed, defines the pointer to the current prediction
   * @type {number}
   */
  @State() highlightedPredictionIndex: number = null;

  /**
   * Signals that an autocompletion search is in progress.
   */
  @Event() runningautocompletionsearch: EventEmitter<string>

  /**
   * Signals that an autocompletion search was completed.
   */
  @Event() completedautocompletionsearch: EventEmitter<AutoCompletionResult>

  /**
   * Signals that an autocompletion search resulted in an error
   */
  @Event() errorautocompletionsearch: EventEmitter<Error>

  /**
   * Signals that a full search is underway
   */
  @Event() runninggeneralsearch: EventEmitter<string>

  /**
   * Signals that a full search was completed
   */
  @Event() completedgeneralsearch: EventEmitter<SearchResult>

  /**
   * Signals that a full search resulted in an error
   */
  @Event() errorgeneralsearch: EventEmitter<Error>


  /**
   * Signals that fetching scopes resulted in an error
   */
  @Event() errorfetchingscopes: EventEmitter<Error>

  constructor() {
    this.handleForminput = this.handleForminput.bind(this)
    this.toggleConfigControl = this.toggleConfigControl.bind(this)
    this.handleActiveScopesChanged = this.handleActiveScopesChanged.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handleSelectAll = this.handleSelectAll.bind(this)
    this.handleInvertSelection = this.handleInvertSelection.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  componentWillLoad= async () => {
    try {
      this.searchState = SearchState.FetchingScopes
      await this.getAvailableScopes()
      this.updateActiveScopes()
      this.searchState = SearchState.FetchingScopes
    } catch (err: any) {
      this.searchState = SearchState.ErrorFetchingScopes
      this.errorfetchingscopes.emit(err)
    }
  }

  componentDidLoad() {
    this.forminputSubscription =
      fromEvent(this.textInput, 'forminput')
      .pipe(debounceTime(200))
      .subscribe(this.handleForminput);
  }

  disconnectedCallback() {
    this.forminputSubscription?.unsubscribe();
  }

  /**
   * When scopes change, update the toggles in the config control
   */
  @Watch('scopes')
  updateActiveScopes() {
    this.activeScopes = new Map()
    this.scopes
    .filter(k => this.availableScopes.includes(k))
    .forEach((scope: string) => {
      this.activeScopes.set(scope, true)
    })
  }

  render() {
    return (
      <Host>
        <div class='wrapper' onKeyDown={this.handleKeyDown}>
          <div class='main-control'>
            <cr-input
              ref={(el) => this.textInput = el as HTMLCrInputElement}
              placeholder={this.placeholder}
            >
              <cr-icon-gear
                slot="icon"
                size={SIZES.MEDIUM}
                onClick={this.toggleConfigControl}
              ></cr-icon-gear>
            </cr-input>
            <cr-button
              kind={KINDS.PRIMARY}
              label={`Search`}
              onFormclick={this.handleSearch}
            ></cr-button>
          </div>
          <div class={`config-control ${this.configControlExpanded ? "expanded" : ""}`}>
            {
              Array.from(this.activeScopes.keys()).length === 0 ?
              (<cr-notification type={NOTIFICATION.ERROR}>No scopes available</cr-notification>) :
              (<cr-vstack class="inner" size={SIZES.MEDIUM}>
                <cr-toggle-list
                  ref={(el) => this.toggleList = el as HTMLCrToggleListElement}
                  items={this.activeScopes}
                  onFormchange={this.handleActiveScopesChanged}
                ></cr-toggle-list>
                <cr-hstack size={SIZES.MEDIUM}>
                  <cr-button
                    size={SIZES.SMALL}
                    label="Select All"
                    onFormclick={this.handleSelectAll}
                  ></cr-button>
                  <cr-button
                    size={SIZES.SMALL}
                    label="Invert Selection"
                    onFormclick={this.handleInvertSelection}
                  ></cr-button>
                </cr-hstack>
              </cr-vstack>)
            }

          </div>
          <div class={`predictions ${this.predictionsExpanded ? "expanded" : ""}`}>
            {this.renderRecommendations()}
          </div>
        </div>
      </Host>
    );
  }

  /**
   * The input field.
   * @type {HTMLCrInputElement}
   */
  private textInput: HTMLCrInputElement

  /**
   * The toggle list that renders available scopes.
   * @type {HTMLCrToggleListElement}
   */
  private toggleList: HTMLCrToggleListElement

  private availableScopes: Array<Scope> = []

  private predictions: Array<Prediction> = []

  private forminputSubscription: Subscription;

  private searchTerm: string

  /**
   * Get scopes that will be made available through the config control
   */
  getAvailableScopes = async (): Promise<Array<Scope>> => {
    try {
      this.availableScopes = await this.driver.getScopes()
      return this.availableScopes
    } catch (e: any) {
      throw e
    }
  }

  /**
   * Expand or collapse the config control
   */
  private toggleConfigControl(): void {
    this.configControlExpanded = !this.configControlExpanded
    if (this.configControlExpanded) {
      this.predictionsExpanded = false
    }
  }

  private handleForminput = async (ev: CustomEvent) : Promise<Array<Prediction>> => {
    this.configControlExpanded = false
    this.predictionsExpanded = true
    const searchTerm: string = ev.detail
    this.searchTerm = ev.detail
    const activeScopes: Array<Scope> = Array.from(this.activeScopes.keys()).filter(k => this.activeScopes.get(k))
    try {
      this.searchState = SearchState.RunningAutocompletionSearch
      this.runningautocompletionsearch.emit(searchTerm)
      this.predictions =  await this.driver.predict(searchTerm, activeScopes)
      this.searchState = SearchState.CompletedAutocompletionSearch
      this.completedautocompletionsearch.emit({
        searchTerm,
        results: this.predictions
      })
      return this.predictions
    } catch (e) {
      if (!e.name || e.name !== "AbortError") {
        this.searchState = SearchState.CompletedAutocompletionSearch;
        this.errorautocompletionsearch.emit(e);
      }
    }

  }

  /**
   * Update the active scopes.
   */
  private handleActiveScopesChanged(event: CustomEvent<Map<string, boolean>>): void {
    this.activeScopes = new Map(event.detail)
  }

  /**
   * Initiate a search request
   */
  private handleSearch(): void {
    this.doSearch()
  }

  /**
   * Select all scopes
   */
  private handleSelectAll(): void {
    this.updateActiveScopes()
  }
  /**
   * Invert active scope selection
   */
  private handleInvertSelection(): void {
    this.toggleList.items.forEach((v, k) => {
      this.activeScopes.set(k, !v)
    })
    this.activeScopes = new Map(this.activeScopes)
  }

  private renderRecommendations = () => {
    let recommendationsView = <div></div>;
    switch(this.searchState) {
      case SearchState.RunningAutocompletionSearch:
        recommendationsView = this.renderRunningAutoCompletionSearchView();
        break;
      case SearchState.CompletedAutocompletionSearch:
        recommendationsView = this.renderCompletedAutoCompletionSearchView();
        break;
      case SearchState.ErrorAutoCompletionSearch:
        recommendationsView = this.renderErrorAutoCompletionSearchView();
        break;
    }
    return recommendationsView;
  }

  private renderRunningAutoCompletionSearchView = () => {
    return(<div class="predictions-list-progress" part="predictions-list-progress">Searching...</div>);
  }

  private renderCompletedAutoCompletionSearchView = () => {
    if (this.predictions.length === 0) {
      return(<div></div>);
    } else {
      return(
        <cr-vstack size={SIZES.MEDIUM}>
          {
            this.predictions.map((prediction: Prediction, predictionIndex: number) => (
              <a
                class={predictionIndex === this.highlightedPredictionIndex ? "highlighted-prediction-link" : "prediction-link"}
                part={predictionIndex === this.highlightedPredictionIndex ? "highlighted-prediction-link" : "prediction-link"}
                href={prediction.url}
              >
                <div class="prediction" part="prediction">
                  {prediction.displayName}
                </div>
              </a>
            ))
          }
        </cr-vstack>
      );
    }
  }

  private renderErrorAutoCompletionSearchView = () => {
    return(<div class="suggestions-list" part="suggestions-list">
      <div class="suggestions-list-error" part="suggestions-list-error">An error occurred. Please try again.</div>
    </div>);
  }

  private handleAutocompletionSearchNavigation = (e: KeyboardEvent) => {
    if (e.code === 'ArrowDown') {
      if (this.highlightedPredictionIndex === null && this.predictions.length > 0) {
        this.highlightedPredictionIndex = 0;
      } else if (this.predictions.length === (this.highlightedPredictionIndex + 1)) {
        this.highlightedPredictionIndex = null;
      } else {
        this.highlightedPredictionIndex += 1;
      }
    } else if (e.code === 'ArrowUp') {
      if (this.highlightedPredictionIndex === null) {
        this.highlightedPredictionIndex = this.predictions.length - 1;
      } else if (this.highlightedPredictionIndex === 0) {
        this.highlightedPredictionIndex = null;
      } else {
        this.highlightedPredictionIndex -= 1;
      }
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (
      ['ArrowDown', 'ArrowUp'].includes(e.code) &&
      this.searchState === SearchState.CompletedAutocompletionSearch
    ) {
      this.handleAutocompletionSearchNavigation(e)
    } else if (e.code === 'Enter') {
      this.handleFormSubmission();
    }
  }

  private handleFormSubmission = () => {
    if (this.highlightedPredictionIndex === null) {
      this.doSearch();
    } else {
      const selectedPrediction = this.predictions[this.highlightedPredictionIndex];
      window.open(selectedPrediction.url, "_self");
    }
  }

  private doSearch = () => {
    let searchTerm = this.searchTerm.trim();
    if (!this.disableSmartFilters) {
      searchTerm = this.filterSearch(searchTerm);
    }
    const activeScopes: Array<Scope> = Array.from(this.activeScopes.keys()).filter(k => this.activeScopes.get(k))
    try {
      this.searchState = SearchState.RunningGeneralSearch;
      this.driver.search(searchTerm, activeScopes)
      .then((searchResult: SearchResultResponse) => {
        this.predictions = [];
        this.searchState = SearchState.CompletedGeneralSearch;
        const eventPayload: SearchResult = {
          searchTerm: searchTerm,
          from: 0,
          size: 10,
          scopes: activeScopes,
          result: searchResult
        };
        this.completedgeneralsearch.emit(eventPayload);
      })
    } catch (err: any) {
      this.searchState = SearchState.CompletedGeneralSearch;
      this.errorgeneralsearch.emit(err)
    }

  }

  private filterSearch = (searchTerm: string): string => {
    const filters: SmartFilter[] = [this.countryCodeFilter]
    return filters.reduce((acc, filterFx) => filterFx(acc), searchTerm)
  }

  private countryCodeFilter: SmartFilter = (searchTerm) => {
    const singlePhoneNumberWithCountryCode = /^\+1\d+$/
    const countryCode = /^\+1/
    if (singlePhoneNumberWithCountryCode.test(searchTerm)) {
      return searchTerm.replace(countryCode, '')
    }
    return searchTerm
  }
}
